<script setup lang="ts">
import { computed, ref } from 'vue'
import { useGlobalStore } from '@/stores/globalStore'
import Notification from '@/layouts/partials/Notification.vue'

const appName = import.meta.env.VITE_APP_NAME ?? 'HonestWaves'
const globalStore = useGlobalStore()

// Default theme-flipped wordmark, same source as the authenticated
// Header's third-tier cascade. We deliberately DON'T do the full
// uploaded-logo/company-name cascade here — the login screen is
// pre-auth, so there's no operating tenant context to derive
// uploaded branding from. Show the shipped wordmark only; fall
// through to plain `appName` text if the asset fails to load.
const logoBase = computed<string>(() =>
  globalStore.currentTheme === 'dark'
    ? '/storage/brand/logo-horizontal-dark'
    : '/storage/brand/logo-horizontal-light',
)
const logoWebp = computed<string>(() => `${logoBase.value}.webp`)
const logoPng = computed<string>(() => `${logoBase.value}.png`)
const logoLoaded = ref<boolean>(true)
const onLogoError = (): void => { logoLoaded.value = false }
</script>

<template>
  <div class="min-h-full flex flex-col items-center justify-center bg-canvas p-6 relative">
    <header class="mb-8 text-center">
      <picture v-if="logoLoaded">
        <source :srcset="logoWebp" type="image/webp" />
        <img
          :src="logoPng"
          :alt="appName"
          class="h-10 w-auto mx-auto"
          @error="onLogoError"
        />
      </picture>
      <h1 v-else class="text-2xl font-bold">{{ appName }}</h1>
    </header>
    <main class="w-full max-w-md">
      <RouterView />
    </main>
    <!-- Native toast host scoped to the layout (`relative` on the
         parent so `absolute` placement anchors here, not the
         viewport). PV driver's `<PrimeVueToastHost />` lives in
         `App.vue` because PrimeVue's `<Toast>` force-teleports to
         body — it's visually anchored to main via CSS vars. -->
    <Notification />
  </div>
</template>
